import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import { Link, useLocation } from 'react-router';

import { Tag } from '@/components/Tag';

import {
  getNextOpenPaths,
  getSelectedHoverStyles,
  getSelectedStyles,
  getShouldBeOpen
} from './utils';
import { useNavigationContext } from '../../context/NavigationContext';
import resolveFullPath from '../../utils/resolveFullPath';

import type { SxProps } from '@mui/material';

export interface NavigationItemData {
  key: string;
  id?: string;
  name?: string;
  icon?: ReactNode;
  path?: string;
  onClick?: () => void;
  children?: NavigationItemData[];
  tag?: string | null;
  alignIconWithTopLevel?: boolean;
  textSx?: SxProps;
}

interface NavigationItemProps {
  item: NavigationItemData;
  level?: number;
  parentPath?: string;
}

const NavigationItem = ({ item, parentPath = '', level = 0 }: NavigationItemProps) => {
  const location = useLocation();
  const { openPaths, setOpenPaths, isMenuOpen, setOpenMenu, close } = useNavigationContext();
  const hasChildren = !!item.children;
  const fullPath = useMemo(() => resolveFullPath(parentPath, item.path), [parentPath, item.path]);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const isAnyDescendantActive = useMemo(() => {
    const check = (node: NavigationItemData, basePath: string): boolean => {
      const nodePath = resolveFullPath(basePath, node.path);
      const hasOwnPath = Boolean(node.path);
      if (
        hasOwnPath &&
        (location.pathname === nodePath || location.pathname.startsWith(`${nodePath}/`))
      )
        return true;
      return node.children?.some(child => check(child, nodePath)) ?? false;
    };
    return check(item, parentPath);
  }, [item, parentPath, location.pathname]);

  const shouldBeOpen = useMemo(
    () =>
      getShouldBeOpen({
        openPaths,
        level,
        isAnyDescendantActive,
        isMenuOpen,
        fullPath,
        hasItemPath: Boolean(item.path)
      }),
    [openPaths, level, item.path, isAnyDescendantActive, fullPath, isMenuOpen]
  );

  const handleClick = useCallback(() => {
    if (level === 0 && !item.path) {
      setOpenMenu(true);
    }

    setOpenPaths(prev => getNextOpenPaths(prev, level, fullPath, isMenuOpen));
  }, [level, setOpenPaths, item.path, setOpenMenu, fullPath, isMenuOpen]);

  const paddingLeftNumber = level === 0 ? 24 : 37 + 12 * level + 8;
  const paddingLeft = `${paddingLeftNumber}px`;

  const isSelected = useMemo(() => {
    if (!hasChildren) {
      return location.pathname === fullPath || location.pathname.startsWith(`${fullPath}/`);
    }
    if (!isMenuOpen) {
      return location.pathname === fullPath || isAnyDescendantActive;
    }

    return location.pathname === fullPath || (isAnyDescendantActive && !shouldBeOpen);
  }, [hasChildren, isMenuOpen, location.pathname, fullPath, isAnyDescendantActive, shouldBeOpen]);

  const handleNonRootItemClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (location.pathname === fullPath) {
        e.preventDefault();
        setOpenMenu(true);
        return;
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setOpenMenu(false);
        timeoutRef.current = null;
      }, 850);
    },
    [setOpenMenu, location.pathname, fullPath]
  );

  const handleRootItemClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      setOpenMenu(true);

      timeoutRef.current = setTimeout(() => {
        setOpenMenu(false);
        timeoutRef.current = null;
      }, 850);
    },
    [setOpenMenu]
  );

  const buttonProps = useMemo(() => {
    if (!hasChildren && item.onClick) {
      return {
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation();
          item.onClick?.();
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          timeoutRef.current = setTimeout(() => {
            close();
            timeoutRef.current = null;
          }, 850);
        }
      };
    }
    if (!hasChildren && item.path) {
      const onClick = level === 0 ? handleRootItemClick : handleNonRootItemClick;

      return {
        component: Link,
        to: item.path,
        onClick
      };
    }
    return { onClick: handleClick };
  }, [hasChildren, item, handleClick, close, level, handleRootItemClick, handleNonRootItemClick]);

  return (
    <ListItem
      disablePadding
      component='div'
      sx={{
        display: 'block',
        ...(level === 0 && { '&:last-child': { mt: 'auto', mb: 0 } }),
        '.MuiListItemButton-root': shouldBeOpen
          ? {
              backgroundColor: '#121836'
            }
          : {}
      }}
    >
      <ListItemButton
        selected={isSelected || shouldBeOpen}
        {...buttonProps}
        sx={{
          gap: '8px',
          minHeight: level === 0 ? 56 : 40,
          pl: paddingLeft,
          pr: 2,
          justifyContent: isMenuOpen ? 'initial' : 'center',
          '&.Mui-selected': getSelectedStyles(isSelected, hasChildren),
          '&.Mui-selected:hover': getSelectedHoverStyles(isSelected)
        }}
      >
        {item.icon && (
          <ListItemIcon
            sx={{
              minWidth: 0,
              justifyContent: 'center',
              position: 'relative',
              right: item.alignIconWithTopLevel && level > 0 ? paddingLeftNumber - 24 : 0
            }}
          >
            {item.icon}
          </ListItemIcon>
        )}

        <ListItemText
          primary={
            <Box
              sx={{
                display: isMenuOpen ? 'flex' : 'none',
                alignItems: 'center',
                fontWeight: isSelected ? 600 : 500,
                gap: '8px',
                position: 'relative',
                right: item.alignIconWithTopLevel && level > 0 ? paddingLeftNumber - 24 : 0
              }}
            >
              <Typography
                noWrap
                sx={{
                  fontSize: level > 0 ? '14px' : '16px',
                  display: 'inline-flex',
                  overflow: 'hidden',
                  ...item.textSx
                }}
              >
                {item.name}
              </Typography>
              {item.tag && (
                <Box
                  sx={{
                    marginLeft: level === 0 ? '16px' : 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Tag label={item.tag} />
                </Box>
              )}

              {hasChildren &&
                isMenuOpen &&
                (shouldBeOpen ? (
                  <ExpandLess sx={{ mb: '-3px' }} />
                ) : (
                  <ExpandMore sx={{ mb: '-3px' }} />
                ))}
            </Box>
          }
        />
      </ListItemButton>

      {isMenuOpen && hasChildren && (
        <Collapse
          unmountOnExit
          in={shouldBeOpen}
          timeout='auto'
        >
          <List
            disablePadding
            component='div'
          >
            {item.children?.map(childItem => (
              <NavigationItem
                key={childItem.key}
                item={childItem}
                level={level + 1}
                parentPath={fullPath}
              />
            ))}
          </List>
        </Collapse>
      )}
    </ListItem>
  );
};

export default NavigationItem;
